import type { Summary } from './types';
import { getChartRuntime } from './chartRuntime';
import { renderBoxPlot } from './box';

export function renderBoxPlotChartJS(summary: Summary, opts?: { min?: number; max?: number; label?: string }): HTMLElement {
  const container = document.createElement('div');
  container.className = 'chart chart--box';

  const values = Array.isArray(summary.values) ? summary.values.filter((v): v is number => typeof v === 'number') : [];
  if (!values.length) return container;

  const canvas = document.createElement('canvas');
  container.appendChild(canvas);

  const ChartRuntime: any = getChartRuntime();

  // Basic stats for overlays and tooltip enrichment
  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const mean = values.reduce((acc, v) => acc + v, 0) / values.length;
  const median = ((): number => {
    if (typeof summary.median === 'number') return summary.median;
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  })();

  // Build a little horizontal jitter for optional raw points overlay
  const jitter = (range = 0.34) => (Math.random() - 0.5) * (range * 2);
  const points = values.map((y) => ({ x: 0 + jitter(0.34), y }));

  let chart: any;
  try {
  // Base chart: just the box plot (least error-prone)
  chart = new ChartRuntime(canvas, {
    type: 'boxplot',
    data: {
      labels: [opts?.label || ''],
      datasets: [
        {
          label: summary.question,
          type: 'boxplot',
          xAxisID: 'x',
          yAxisID: 'y',
          data: [values],
          backgroundColor: 'rgba(37, 99, 235, 0.15)',
          borderColor: 'rgba(37, 99, 235, 0.55)'
        }
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300 },
      scales: {
        x: { display: false, grid: { display: false }, type: 'category' as const },
        y: {
          min: typeof opts?.min === 'number' ? opts!.min : undefined,
          max: typeof opts?.max === 'number' ? opts!.max : undefined,
          ticks: { color: '#475569', font: { size: 12 } },
          grid: { color: 'rgba(148, 163, 184, 0.18)', drawBorder: false },
        },
      },
      plugins: { legend: { display: false } },
    },
  });
  
  // Try to add overlays (jittered points + mean/median lines). If anything fails, keep the base chart.
  try {
    // Add hidden x scale for overlays
    (chart.options.scales as any).xp = { type: 'linear', display: false, min: -0.5, max: 0.5, grid: { display: false } };
    chart.data.datasets.push(
      {
        label: 'Responses',
        type: 'scatter' as const,
        xAxisID: 'xp',
        yAxisID: 'y',
        data: points as any,
        pointRadius: 2,
        pointHoverRadius: 3,
        pointBackgroundColor: 'rgba(37, 99, 235, 0.55)',
        pointBorderWidth: 0,
        showLine: false,
        order: 3,
      } as any,
      {
        label: 'Median',
        type: 'line' as const,
        xAxisID: 'xp',
        yAxisID: 'y',
        data: [ { x: -0.44, y: median }, { x: 0.44, y: median } ] as any,
        borderColor: 'rgba(14, 165, 233, 1)',
        borderWidth: 2,
        pointRadius: 0,
        order: 2,
      } as any,
      {
        label: 'Mean',
        type: 'line' as const,
        xAxisID: 'xp',
        yAxisID: 'y',
        data: [ { x: -0.44, y: mean }, { x: 0.44, y: mean } ] as any,
        borderColor: 'rgba(234, 179, 8, 1)',
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        order: 2,
      } as any,
    );
    chart.options.plugins = chart.options.plugins || {} as any;
    (chart.options.plugins as any).legend = {
      display: true,
      position: 'bottom',
      labels: {
        color: '#475569',
        usePointStyle: false,
        filter: (item: any) => {
          const label = typeof item.text === 'string' ? item.text : '';
          return label === 'Mean' || label === 'Median';
        },
      },
    } as any;
    (chart.options.plugins as any).tooltip = {
      callbacks: {
        label(ctx: any) {
          const dsType = ctx.dataset.type || ctx.chart.config.type;
          const y = ctx.parsed?.y;
          if (dsType === 'boxplot') {
            const r = ctx.raw || {};
            const parts: string[] = [];
            if (typeof r.min === 'number') parts.push(`Min: ${r.min}`);
            if (typeof r.whiskerLow === 'number') parts.push(`Whisker low: ${r.whiskerLow}`);
            if (typeof r.q1 === 'number') parts.push(`Q1: ${r.q1}`);
            if (typeof r.median === 'number') parts.push(`Median: ${r.median}`);
            if (typeof r.q3 === 'number') parts.push(`Q3: ${r.q3}`);
            if (typeof r.whiskerHigh === 'number') parts.push(`Whisker high: ${r.whiskerHigh}`);
            if (typeof r.max === 'number') parts.push(`Max: ${r.max}`);
            parts.push(`Mean: ${Number.isFinite(mean) ? mean.toFixed(1) : ''}`);
            return parts;
          }
          if (ctx.dataset.label === 'Responses') return `Value: ${y}`;
          if (ctx.dataset.label === 'Mean') return `Mean: ${y}`;
          if (ctx.dataset.label === 'Median') return `Median: ${y}`;
          return `${ctx.dataset.label}: ${y}`;
        },
      },
    } as any;
    chart.update();
  } catch {
    // ignore overlays if environment doesn't support mixed datasets
  }
  } catch (err) {
    // Fallback to simplified SVG box plot if Chart.js or plugin errors out
    try {
      container.innerHTML = '';
      container.appendChild(renderBoxPlot(summary));
      return container;
    } catch {}
    return container;
  }

  (globalThis as any).__activeCharts = (globalThis as any).__activeCharts || [];
  (globalThis as any).__activeCharts.push(chart);
  return container;
}
